import React from 'react';

import { useTranslation } from 'react-i18next';

import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import {
  ChargePartial,
  Invoice,
  TaxPolicy,
} from 'lane-shared/domains/billingPayments/types';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';
import {
  getPricing,
  PricingParams,
} from 'domains-billing-calculation';
import { Currency, formatCurrencyWithSymbolOnly, formatCurrencyCodeAndSymbol } from 'domains-billing-currency';
import {
  graphQLToCurrency,
  currencyToPaymentCurrency,
} from 'domains-billing-web-mappers';

import { M, H4 } from 'components/typography';

import useUserLocale from 'hooks/useUserLocale';

import styles from './styles.scss';

export function InvoiceTotal({
  invoice,
  charges,
}: {
  invoice: Invoice | undefined;
  charges: ChargePartial[];
}) {
  const { t } = useTranslation();
  const locale = useUserLocale();
  const currency = invoice?.currency
    ? graphQLToCurrency(invoice.currency)
    : Currency.USD;
  const currencyCode = currencyToPaymentCurrency(currency);
  const { taxDetails } = useBillingPaymentSettingsForChannel({
    channelId: invoice?.groupId,
  });
  const isAdminFeeFeatureEnabled = useBpAdminFeeChargeCodeEnabled();
  const taxTotalLabel = t('abp.invoice.details.totalPriceIncludingTax', {
    taxName: taxDetails?.taxName || t('abp.invoice.details.tax').toLowerCase(),
  });

  let invoiceSubtotal = 0;
  let invoiceTax = 0;
  let invoiceTotal = 0;
  const taxInclusive = taxDetails?.taxPolicy === TaxPolicy.INCLUSIVE;

  charges.forEach(charge => {
    // skip charge if voided
    if (charge.status === 'VOIDED') return;

    // no items
    if (charge.items.length === 0) {
      invoiceSubtotal += charge.amount;
      invoiceTotal += charge.amount;

      return;
    }

    charge.items.forEach(item => {
      const params: PricingParams = {
        currency: currencyCode,
        locale,
        priceComponents: item.product,
        // Preserve prior exclusive math when the flag is off so invoice totals
        // remain unchanged; inclusive subtotal is recomputed below via the
        // `invoiceSubtotal += invoiceTax` adjustment.
        ...(isAdminFeeFeatureEnabled
          ? { inclusivePricing: taxInclusive, isAdminFeeFeatureEnabled: true }
          : {}),
      };

      const { grossTax, grossAdminFeeTax, listPrice } = getPricing(params);

      // Mirror the flag-gating used by generatePDFInvoice.ts so the web UI
      // stays in sync with the PDF when the flag is off but the backend
      // persisted a non-zero grossAdminFeeTax from a prior flag-on period.
      const itemTax =
        grossTax + (isAdminFeeFeatureEnabled ? grossAdminFeeTax : 0);

      invoiceSubtotal += listPrice * item.quantity;
      invoiceTax += itemTax * item.quantity;
      invoiceTotal += (listPrice + itemTax) * item.quantity;
    });
  });

  if (taxInclusive) {
    invoiceSubtotal += invoiceTax;
  }

  return (
    <div className={styles.invoiceTotalContainer}>
      <div className={styles.invoiceTotalGrid}>
        <M className={styles.item}>{t('abp.invoice.details.subtotal')}</M>
        <M className={styles.item}>{formatCurrencyWithSymbolOnly(currency, invoiceSubtotal, locale)}</M>
        <M className={styles.item}>
          {taxDetails?.taxName || t('abp.invoice.details.tax')}
          {taxInclusive ? ` ${t('abp.invoice.details.tax.included')}` : ''}
        </M>
        <M className={styles.item}>{formatCurrencyWithSymbolOnly(currency, invoiceTax, locale)}</M>
        <H4 className={styles.item}>{taxTotalLabel}</H4>
        <div className={styles.invoiceTotal}>
          <H4 className={styles.item}>
            {formatCurrencyCodeAndSymbol(currency, invoiceTotal, locale)}
          </H4>
        </div>
      </div>
    </div>
  );
}
